import type { Assignment, Person, PlanDetail, PlanItem, PlanSummary, Role } from "../../../shared/types";
import { aucklandLocalToUtc, formatDay, utcToAucklandInput } from "../../../shared/time";

const RANK: Record<Role, number> = {
  volunteer: 1,
  member: 2,
  coordinator: 3,
  leader: 4,
  owner: 5,
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const POSITION_HINTS: Record<string, string[]> = {
  keys: ["keys", "piano", "keyboard"],
  "acoustic guitar": ["guitar", "acoustic"],
  "electric guitar": ["guitar", "electric"],
  bass: ["bass"],
  drums: ["drums", "percussion"],
  lead: ["vocals", "voice"],
  harmony: ["vocals", "voice", "harmony"],
  sound: ["sound", "audio"],
  lyrics: ["lyrics", "projection", "visual"],
  host: ["host", "hospitality"],
  welcome: ["welcome", "hospitality"],
  leader: ["leader", "prayer"],
};

export const ITEM_DEFAULT_SECONDS: Record<PlanItem["kind"], number> = {
  song: 240,
  header: 60,
  reading: 90,
  media: 180,
  break: 30,
};

export const KIND_LABEL: Record<PlanItem["kind"], string> = {
  song: "Song",
  header: "Header",
  reading: "Reading",
  media: "Media",
  break: "Break",
};

export type BlockKind = Exclude<PlanItem["kind"], "song">;

export interface WeekWindow {
  from: string;
  to: string;
  label: string;
}

export interface ScheduleCell {
  assignmentId: string;
  personId: string;
  personName: string;
  planId: string;
  planTitle: string;
  team: string;
  position: string;
  status: Assignment["status"];
  conflicts: string[];
  responseNote: string;
}

export interface ScheduleRow {
  personId: string;
  personName: string;
  cells: ScheduleCell[];
}

export interface OpenSeat {
  planId: string;
  planTitle: string;
  startsAt: string;
  team: string;
  position: string;
}

export interface ScheduleView {
  rows: ScheduleRow[];
  openings: OpenSeat[];
  conflicts: ScheduleCell[];
}

export function canLead(role: Role | undefined): boolean {
  if (!role) return false;
  return RANK[role] >= RANK.leader;
}

export function messageOf(caught: unknown): string {
  if (caught instanceof Error && caught.message) return caught.message;
  return "Something went wrong";
}

export function stageColor(color: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : "#8b6cff";
}

export function standHref(planId: string, itemId?: string): string {
  if (!itemId) return `/stand/${encodeURIComponent(planId)}`;
  return `/stand/${encodeURIComponent(planId)}?item=${encodeURIComponent(itemId)}`;
}

export function statusTone(status: string): "teal" | "amber" | "danger" | "mute" | "violet" {
  if (status === "accepted" || status === "confirmed") return "teal";
  if (status === "pending" || status === "draft") return "amber";
  if (status === "declined") return "danger";
  return "mute";
}

export function kindTone(kind: PlanItem["kind"]): "violet" | "teal" | "amber" | "mute" {
  if (kind === "song") return "violet";
  if (kind === "header") return "teal";
  if (kind === "media") return "amber";
  return "mute";
}

export function aucklandDate(iso: string): string {
  return utcToAucklandInput(iso).slice(0, 10);
}

export function shiftDate(localDate: string, days: number): string {
  const utc = aucklandLocalToUtc(`${localDate}T12:00`);
  const shifted = new Date(new Date(utc).getTime() + days * 86_400_000).toISOString();
  return aucklandDate(shifted);
}

export function localInputToUtc(value: string): string | null {
  try {
    return aucklandLocalToUtc(value);
  } catch {
    return null;
  }
}

export function defaultPlanStart(): string {
  return `${shiftDate(aucklandDate(new Date().toISOString()), 1)}T10:00`;
}

export function shiftLocalInput(local: string, days: number): string {
  const time = local.slice(11, 16) || "10:00";
  return `${shiftDate(local.slice(0, 10), days)}T${time}`;
}

export function weekWindow(anchorIso: string): WeekWindow {
  const day = aucklandDate(anchorIso);
  const noon = aucklandLocalToUtc(`${day}T12:00`);
  const short = new Intl.DateTimeFormat("en-US", { timeZone: "Pacific/Auckland", weekday: "short" }).format(new Date(noon));
  const index = WEEKDAYS.indexOf(short);
  const mondayOffset = index <= 0 ? -6 : 1 - index;
  const monday = shiftDate(day, index < 0 ? 0 : mondayOffset);
  const sunday = shiftDate(monday, 6);
  const next = shiftDate(monday, 7);
  return {
    from: aucklandLocalToUtc(`${monday}T00:00`),
    to: aucklandLocalToUtc(`${next}T00:00`),
    label: `${formatDay(aucklandLocalToUtc(`${monday}T12:00`))} – ${formatDay(aucklandLocalToUtc(`${sunday}T12:00`))}`,
  };
}

export function splitDuration(total: number): { minutes: number; seconds: number } {
  const safe = Math.max(0, Math.round(total));
  return { minutes: Math.floor(safe / 60), seconds: safe % 60 };
}

export function joinDuration(minutes: number, seconds: number): number | null {
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
  const wholeMinutes = Math.trunc(minutes);
  const wholeSeconds = Math.trunc(seconds);
  if (wholeMinutes < 0 || wholeSeconds < 0 || wholeSeconds > 59) return null;
  const total = wholeMinutes * 60 + wholeSeconds;
  if (total > 8 * 60 * 60) return null;
  return total;
}

export function parseDuration(minutes: string, seconds: string): number | null {
  if (!/^\d{1,3}$/.test(minutes.trim()) || !/^\d{1,2}$/.test(seconds.trim())) return null;
  return joinDuration(Number(minutes), Number(seconds));
}

export function moveId(ids: string[], index: number, delta: number): string[] | null {
  const target = index + delta;
  if (index < 0 || target < 0 || index >= ids.length || target >= ids.length) return null;
  const moving = ids[index];
  if (!moving) return null;
  const without = ids.filter((_, itemIndex) => itemIndex !== index);
  return [...without.slice(0, target), moving, ...without.slice(target)];
}

export function reorderIds(ids: string[], draggedId: string, targetId: string): string[] | null {
  const from = ids.indexOf(draggedId);
  const to = ids.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return null;
  const moving = ids[from];
  if (!moving) return null;
  const without = ids.filter((id) => id !== draggedId);
  return [...without.slice(0, to), moving, ...without.slice(to)];
}

export function groupPlansByDay(plans: PlanSummary[]): Array<{ label: string; plans: PlanSummary[] }> {
  const groups: Array<{ label: string; plans: PlanSummary[] }> = [];
  for (const plan of plans) {
    const label = formatDay(plan.startsAt);
    const last = groups.at(-1);
    if (last && last.label === label) groups[groups.length - 1] = { label, plans: [...last.plans, plan] };
    else groups.push({ label, plans: [plan] });
  }
  return groups;
}

export function countPending(plan: PlanDetail): number {
  return plan.teams
    .flatMap((team) => team.positions.flatMap((position) => position.assignments))
    .filter((assignment) => assignment.status === "pending").length;
}

export function countSongs(plan: PlanDetail): number {
  return plan.items.filter((item) => item.kind === "song").length;
}

export function activeFirst(people: Person[]): Person[] {
  return people.toSorted((left, right) => {
    if (left.status !== right.status) return left.status === "active" ? -1 : 1;
    return left.name.localeCompare(right.name, "en-NZ");
  });
}

export function peopleForPosition(people: Person[], positionName: string): { suggested: Person[]; others: Person[] } {
  const hints = POSITION_HINTS[positionName.trim().toLowerCase()] ?? [positionName.trim().toLowerCase()];
  const suggested = people.filter((person) => person.instruments.some((instrument) => {
    const value = instrument.toLowerCase();
    return hints.some((hint) => value.includes(hint) || hint.includes(value));
  }));
  const suggestedIds = new Set(suggested.map((person) => person.id));
  return { suggested, others: people.filter((person) => !suggestedIds.has(person.id)) };
}

export function buildSchedule(plans: PlanDetail[]): ScheduleView {
  const cells = plans.flatMap((plan) => plan.teams.flatMap((team) => team.positions.flatMap((position) => (
    position.assignments.map((assignment) => ({
      assignmentId: assignment.id,
      personId: assignment.personId,
      personName: assignment.personName,
      planId: plan.id,
      planTitle: plan.title,
      team: team.name,
      position: position.name,
      status: assignment.status,
      conflicts: assignment.conflicts,
      responseNote: assignment.responseNote,
    }))
  ))));
  const grouped = new Map<string, ScheduleRow>();
  for (const cell of cells) {
    const current = grouped.get(cell.personId) ?? { personId: cell.personId, personName: cell.personName, cells: [] };
    grouped.set(cell.personId, { ...current, cells: [...current.cells, cell] });
  }
  const openings = plans.flatMap((plan) => plan.teams.flatMap((team) => team.positions
    .filter((position) => !position.assignments.some((assignment) => assignment.status !== "declined"))
    .map((position) => ({
      planId: plan.id,
      planTitle: plan.title,
      startsAt: plan.startsAt,
      team: team.name,
      position: position.name,
    }))));
  return {
    rows: [...grouped.values()].toSorted((left, right) => left.personName.localeCompare(right.personName, "en-NZ")),
    openings: openings.toSorted((left, right) => left.startsAt.localeCompare(right.startsAt)),
    conflicts: cells.filter((cell) => cell.conflicts.length > 0),
  };
}
